/**
 * Offline conversie-import voor Google Ads: betaalde bestellingen die aan een advertentieklik
 * hangen, in het CSV-formaat dat Google verwacht ("Conversies uploaden" → Bestand).
 *
 * Waarom dit nodig is: de aankoop-conversie vuurt in de browser op de bedankpagina. Betaalt een
 * klant in zijn bankapp en komt hij daarna niet terug, dan ziet Google Ads die omzet nooit —
 * terwijl de server hem wél kent. Dit bestand maakt van die orders alsnog een conversie.
 *
 * DUBBELTELLEN VOORKOMEN. Twee grendels:
 *  1. `adsExportedAt` op de order — wat één keer geëxporteerd is, komt niet nog eens mee;
 *  2. de conversieactie waarin je importeert moet een ÁNDERE zijn dan de website-tag
 *     ("Aankoop (2)"). Anders telt een klant die wél terugkwam twee keer: één keer via de tag en
 *     één keer via dit bestand. Zie de instructie in `adsImportHandleiding`.
 */
import type { Cents } from "./types/money";

export interface AdsConversieOrder {
  /** Ordernummer zoals de klant het ziet — alleen voor de admin-lijst, niet voor Google. */
  label: string;
  clickId?: string;
  clickIdType?: "gclid" | "gbraid" | "wbraid";
  /** Wanneer de betaling bevestigd is (epoch ms) — dat is het conversiemoment. */
  paidAt?: number;
  /** Ordertotaal inclusief btw. */
  totalCents: Cents;
  adsExportedAt?: number;
}

export interface AdsConversieRegel {
  clickIdType: "gclid" | "gbraid" | "wbraid";
  clickId: string;
  label: string;
  /** "jjjj-mm-dd uu:mm:ss" in de tijdzone uit de header. */
  tijd: string;
  /** Bedrag met punt als decimaalteken (Google wil het Engelse formaat). */
  waarde: string;
}

/** Tijdzone die in de bestandsheader gaat; Google leest de tijden dan in die zone. */
export const ADS_TIJDZONE = "Europe/Amsterdam";

/**
 * Welke orders horen in de export? Betaald, met een klik-ID, nog niet eerder geëxporteerd, en
 * binnen Googles importvenster: de conversie moet ná de klik liggen en Google accepteert niets
 * ouder dan 90 dagen.
 */
export function selecteerAdsConversies(orders: AdsConversieOrder[], nu: number): AdsConversieOrder[] {
  const grens = nu - 90 * 24 * 60 * 60 * 1000;
  return orders.filter(
    (o) => !!o.clickId && !o.adsExportedAt && o.paidAt != null && o.paidAt >= grens && o.paidAt <= nu && o.totalCents > 0,
  );
}

/** Epoch-ms → "jjjj-mm-dd uu:mm:ss" in de opgegeven tijdzone (Google's formaat). */
export function adsTijd(ms: number, tijdzone: string = ADS_TIJDZONE): string {
  const f = new Intl.DateTimeFormat("sv-SE", {
    timeZone: tijdzone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  // "sv-SE" geeft al "2026-08-15 14:30:00" — precies wat Google wil.
  return f.format(new Date(ms)).replace("T", " ");
}

export function adsConversieRegels(orders: AdsConversieOrder[], nu: number): AdsConversieRegel[] {
  return selecteerAdsConversies(orders, nu).map((o) => ({
    clickIdType: o.clickIdType ?? "gclid",
    clickId: o.clickId!,
    label: o.label,
    tijd: adsTijd(o.paidAt!),
    waarde: (o.totalCents / 100).toFixed(2),
  }));
}

/**
 * Het complete CSV-bestand. Google verwacht een parameterregel bovenaan, dan de kolomkoppen.
 * De drie klik-ID-soorten hebben elk een eigen kolom; per regel is er precies één gevuld.
 */
export function adsConversieCsv(orders: AdsConversieOrder[], conversieNaam: string, nu: number): string {
  const regels = adsConversieRegels(orders, nu);
  const kop = [
    `Parameters:TimeZone=${ADS_TIJDZONE}`,
    "Google Click ID,Conversion Name,Conversion Time,Conversion Value,Conversion Currency",
  ];
  const body = regels.map((r) =>
    [r.clickId, csvVeld(conversieNaam), r.tijd, r.waarde, "EUR"].join(","),
  );
  return [...kop, ...body].join("\n") + "\n";
}

/** Komma of aanhalingsteken in een veld → tussen aanhalingstekens, conform CSV. */
function csvVeld(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Korte instructie die naast de exportknop staat, zodat niemand hoeft te gokken. */
export const adsImportHandleiding = [
  "Maak in Google Ads eerst een conversieactie met bron 'Importeren' → 'Bestanden uploaden'.",
  "Geef die een eigen naam (bijvoorbeeld 'Aankoop (import)') en vul die naam hieronder in.",
  "Importeer NOOIT in de website-actie 'Aankoop (2)': klanten die wél terugkwamen tellen dan dubbel.",
  "Upload het bestand via Doelen → Conversies → Uploads.",
] as const;

export interface AdsMaandOrder {
  /** Wanneer de betaling bevestigd is (epoch ms). Zonder betaling telt een order niet mee. */
  paidAt?: number;
  clickId?: string;
  /** Netto omzet excl. btw — creditnota's zijn er al afgetrokken door de aanroeper. */
  omzetExclCents: Cents;
}

export interface AdsMaand {
  /** `2026-10`, in Nederlandse tijd. */
  maand: string;
  orders: number;
  adsOrders: number;
  omzetExclCents: Cents;
  adsOmzetExclCents: Cents;
}

/**
 * Omzet per maand, gesplitst naar "kwam binnen via een advertentieklik" en de rest.
 *
 * **Waarom dit naast GA4 bestaat.** In GA4 heeft bijna de helft van de aankopen geen herkomst:
 * wie analytische cookies weigert, levert geen sessie op en belandt in "(not set)" — 20 van de 45
 * aankopen in oktober 2026. Daarmee is GA4 ongeschikt om te beslissen of advertenties geld
 * opleveren. Het klik-ID staat op de bestelling zélf en is er dus ook als de klant geen cookies
 * wilde. Die vraag beantwoorden we daarom uit onze eigen gegevens.
 *
 * **Lees het als een ondergrens, niet als de waarheid.** Een klik-ID zit er alleen op als de
 * bezoeker via de advertentielink binnenkwam. Wie de advertentie zag, later zelf de naam intikte
 * en toen pas bestelde, telt hier niet mee — terwijl die bestelling er zonder de advertentie
 * misschien niet was geweest. Het echte aandeel ligt dus hóger dan wat hier staat.
 */
export function advertentieOmzetPerMaand(orders: AdsMaandOrder[]): AdsMaand[] {
  const fmt = new Intl.DateTimeFormat("nl-NL", { timeZone: ADS_TIJDZONE, year: "numeric", month: "2-digit" });
  const per = new Map<string, AdsMaand>();
  for (const o of orders) {
    if (o.paidAt == null) continue;
    const d = fmt.formatToParts(new Date(o.paidAt));
    const jaar = d.find((p) => p.type === "year")?.value ?? "";
    const maand = `${jaar}-${d.find((p) => p.type === "month")?.value ?? ""}`;
    const r = per.get(maand) ?? { maand, orders: 0, adsOrders: 0, omzetExclCents: 0, adsOmzetExclCents: 0 };
    r.orders += 1;
    r.omzetExclCents += o.omzetExclCents;
    if (o.clickId) {
      r.adsOrders += 1;
      r.adsOmzetExclCents += o.omzetExclCents;
    }
    per.set(maand, r);
  }
  // Nieuwste maand bovenaan: daar kijk je naar als je een budget moet bijstellen.
  return [...per.values()].sort((a, b) => b.maand.localeCompare(a.maand));
}
